import _ from 'lodash'
import { createSelector } from 'reselect'

import { RootState } from '../../types'
import { paramSelector } from '../../../utils/selectors'

import { WithLoading } from '../../loading'

import { ImportDeliveryOption, ImportProductsState, Product, ProductItem, Search } from '.'

export const selectState = createSelector(
  (s: RootState) => s.importCheckProducts,
  b => b
)

export const areProductsImportItemsLoading = createSelector(selectState, s => s.loading)

export const getProductsImportItems = createSelector(selectState, s => s.data)

export const getProductsImportId = createSelector(selectState, s => s.importId)

export const getProductsAreAllEnabled = createSelector(selectState, s => s.data.allEnabled)

export const getSelectedSearch = createSelector(selectState, paramSelector<{ itemId: string }>(), (state, { itemId }) =>
  findSelectedSearch(state, itemId)
)

export const getSelectedSearches = createSelector(selectState, s =>
  _.reduce(
    s.data.items,
    (acc, i) => {
      const selectedSearch = findSelectedSearch(s, i.id)
      return selectedSearch ? { ...acc, [i.id]: selectedSearch } : acc
    },
    {}
  )
)

export const getSelectedProduct = createSelector(
  selectState,
  paramSelector<{ itemId: string }>(),
  (state, { itemId }) => findSelectedProduct(state, itemId)
)

export const getSelectedProducts = createSelector(selectState, s =>
  _.reduce(
    s.data.items,
    (acc, i) => {
      const selectedProduct = findSelectedProduct(s, i.id)
      return selectedProduct ? { ...acc, [i.id]: selectedProduct } : acc
    },
    {}
  )
)

export const getSelectedDeliveryOption = createSelector(
  selectState,
  paramSelector<{ itemId: string }>(),
  (state, { itemId }) => findSelectedDeliveryOption(state, itemId)
)

export const getSelectedDeliveryOptions = createSelector(selectState, s =>
  _.reduce(
    s.data.items,
    (acc, i) => {
      const selectedDeliveryOption = findSelectedDeliveryOption(s, i.id)
      return selectedDeliveryOption ? { ...acc, [i.id]: selectedDeliveryOption } : acc
    },
    {}
  )
)

export const getSelectedLanguage = createSelector(
  selectState,
  paramSelector<{ itemId: string }>(),
  (state, { itemId }) => findSelectedLanguage(state, itemId)
)

export const getSelectedLanguages = createSelector(selectState, s =>
  _.reduce(
    s.data.items,
    (acc, i) => {
      const selectedLanguage = findSelectedLanguage(s, i.id)
      return selectedLanguage ? { ...acc, [i.id]: selectedLanguage } : acc
    },
    {}
  )
)

const findRelatedItem = (state: WithLoading<ImportProductsState>, itemId: string) =>
  _.find(state.data.items, (i: ProductItem) => i.id === itemId)

const findSelectedSearch = (state: WithLoading<ImportProductsState>, itemId: string) => {
  const itemSearches = findRelatedItem(state, itemId)?.searches
  return _.find(itemSearches, (s: Search) => s.enabled) ?? _.head(itemSearches)
}

const findSelectedProduct = (state: WithLoading<ImportProductsState>, itemId: string) => {
  const products =
    findSelectedSearch(state, itemId)?.products ?? findRelatedItem(state, itemId)?.investigation?.products
  return _.find(products, (s: Product) => s.enabled) ?? _.head(products)
}

const findSelectedDeliveryOption = (state: WithLoading<ImportProductsState>, itemId: string) => {
  const deliveryOptions = findSelectedProduct(state, itemId)?.deliveryOptions
  return _.find(deliveryOptions, (ido: ImportDeliveryOption) => ido.enabled) ?? _.head(deliveryOptions)
}

const findSelectedLanguage = (state: WithLoading<ImportProductsState>, itemId: string) => {
  const languages = findSelectedProduct(state, itemId)?.languages
  return _.findKey(languages) ?? _.head(_.keys(languages))
}
